import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from './client';

export interface RankedCandidate {
  patient_id: string;
  score: number;
  name_score: number;
  dob_match: boolean;
  postal_prefix_match: boolean;
  given_name: string;
  family_name: string;
  dob: string;
}

export interface MergeCandidateProduct {
  id: string;
  code: string;
  display_name: string;
}

export interface PatientMergeCandidate {
  id: string;
  organisation_id: string;
  organisation_name: string | null;
  provisional_patient_id: string;
  provisional: {
    given_name: string;
    family_name: string;
    dob: string;
    postal_code: string | null;
  };
  products: MergeCandidateProduct[];
  candidates: RankedCandidate[];
  match_features: Record<string, unknown>;
  status: 'pending_review' | 'merged' | 'dismissed';
  created_at: string;
}

export interface PatientMergeCandidateList {
  items: PatientMergeCandidate[];
}

// `orgId` is optional — passing `undefined` fetches the cross-org
// queue so the Duplicate users page can list everything on first
// open. The orgId still threads through to the URL query when
// supplied, so per-org views are unchanged.
export function useMergeCandidates(orgId: string | undefined) {
  return useQuery({
    queryKey: ['patient-merge-candidates', orgId ?? 'all'],
    queryFn: () => {
      const qs = orgId ? `?org=${encodeURIComponent(orgId)}` : '';
      return apiGet<PatientMergeCandidateList>(`/api/patient-merge-candidates${qs}`);
    },
  });
}

// Platform-wide count of pending merge candidates. Drives the
// "Duplicate users" navbar badge. Polled at a slower cadence than
// the dashboard tiles — it changes only when a new patient is
// flagged or an admin reviews one, neither of which is high-rate.
export function useMergeCandidatesCount() {
  return useQuery({
    queryKey: ['patient-merge-candidates', 'count'],
    queryFn: () => apiGet<{ count: number }>('/api/patient-merge-candidates/count'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

export interface MergeInput {
  candidate_id: string;
  org_id: string;
  surviving_patient_id: string;
  reason?: string;
}

export function useMergeFromCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MergeInput) =>
      apiPost<void>(
        `/api/patient-merge-candidates/${input.candidate_id}/merge?org=${encodeURIComponent(input.org_id)}`,
        {
          surviving_patient_id: input.surviving_patient_id,
          reason: input.reason,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient-merge-candidates'] });
      qc.invalidateQueries({ queryKey: ['clinical-patients', 'list'] });
      qc.invalidateQueries({ queryKey: ['platform-users', 'list'] });
    },
  });
}

export interface DismissInput {
  candidate_id: string;
  org_id: string;
  reason?: string;
}

export function useDismissCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DismissInput) =>
      apiPost<void>(
        `/api/patient-merge-candidates/${input.candidate_id}/dismiss?org=${encodeURIComponent(input.org_id)}`,
        { reason: input.reason },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient-merge-candidates'] });
      qc.invalidateQueries({ queryKey: ['clinical-patients', 'list'] });
    },
  });
}

export interface MergeHistoryActor {
  display: string;
}
export interface AbsorbedMergeRecord {
  merge_id: string;
  absorbed_patient_id: string;
  absorbed_snapshot: { given_name: string; family_name: string; dob: string };
  merged_at: string;
  reason: string | null;
  actor: MergeHistoryActor;
}
export interface MergedIntoRecord {
  surviving_patient_id: string;
  surviving_name: string | null;
  merged_at: string;
  reason: string | null;
  actor: MergeHistoryActor;
}
export interface PatientMergeHistoryView {
  absorbed_records: AbsorbedMergeRecord[];
  merged_into?: MergedIntoRecord;
}

// Merge history for one patient. `orgId` comes from the loaded patient
// (organisation_id) — the endpoint needs it to pick the org-sharded DB.
export function usePatientMergeHistory(id: string | undefined, orgId: string | undefined) {
  return useQuery({
    queryKey: ['patient-merge-history', id ?? 'idle'],
    queryFn: () =>
      apiGet<PatientMergeHistoryView>(
        `/api/patients/${id}/merge-history?org=${encodeURIComponent(orgId ?? '')}`,
      ),
    enabled: !!id && !!orgId,
  });
}
